enum PlaceApiStatus {
    OK = 'OK',
    NOT_FOUND = 'NOT_FOUND',
    INVALID_REQUEST = 'INVALID_REQUEST',
    MAX_ROUTE_LENGTH_EXCEEDED = 'MAX_ROUTE_LENGTH_EXCEEDED',
    MAX_WAYPOINTS_EXCEEDED = 'MAX_WAYPOINTS_EXCEEDED',
    OVER_DAILY_LIMIT = 'OVER_DAILY_LIMIT',
    OVER_QUERY_LIMIT = 'OVER_QUERY_LIMIT',
    REQUEST_DENIED = 'REQUEST_DENIED',
    UNKNOWN_ERROR = 'UNKNOWN_ERROR',
    ZERO_RESULTS = 'ZERO_RESULTS',
    TECH_ERROR = 'TECH_ERROR',
}

type PredictionSubstring = {
    length: number;
    offset: number;
};

type StructuredFormatting = {
    main_text: string;
    main_text_matched_substrings: PredictionSubstring[];
    secondary_text: string;
};

type PredictionTerm = {
    offset: number;
    value: string;
};

enum AddressType {
    administrative_area_level_1 = 'administrative_area_level_1',
    administrative_area_level_2 = 'administrative_area_level_2',
    administrative_area_level_3 = 'administrative_area_level_3',
    administrative_area_level_4 = 'administrative_area_level_4',
    administrative_area_level_5 = 'administrative_area_level_5',
    airport = 'airport',
    colloquial_area = 'colloquial_area',
    country = 'country',
    establishment = 'establishment',
    intersection = 'intersection',
    locality = 'locality',
    natural_feature = 'natural_feature',
    neighborhood = 'neighborhood',
    park = 'park',
    point_of_interest = 'point_of_interest',
    political = 'political',
    postal_code = 'postal_code',
    premise = 'premise',
    route = 'route',
    street_address = 'street_address',
    sublocality = 'sublocality',
    sublocality_level_1 = 'sublocality_level_1',
    sublocality_level_2 = 'sublocality_level_2',
    sublocality_level_3 = 'sublocality_level_3',
    sublocality_level_4 = 'sublocality_level_4',
    sublocality_level_5 = 'sublocality_level_5',
    subpremise = 'subpremise',
    ward = 'ward',
}

type PlaceAutocompleteResult = {
    description: string;
    distance_meters: number;
    matched_substrings: PredictionSubstring[];
    place_id: string;
    structured_formatting: StructuredFormatting;
    terms: PredictionTerm[];
    types: AddressType[];
};

type PlaceAutocompleteResponse = {
    status: PlaceApiStatus;
    predictions: PlaceAutocompleteResult[];
};

enum GeocodingAddressComponentType {
    bus_station = 'bus_station',
    establishment = 'establishment',
    floor = 'floor',
    parking = 'parking',
    point_of_interest = 'point_of_interest',
    post_box = 'post_box',
    postal_town = 'postal_town',
    room = 'room',
    street_number = 'street_number',
    train_station = 'train_station',
    transit_station = 'transit_station',
}

type AddressComponent = {
    long_name: string;
    short_name: string;
    types: AddressType[] | GeocodingAddressComponentType[];
};

type LatLngLiteral = {
    lat: number;
    lng: number;
};

type LatLngBounds = {
    northeast: LatLngLiteral;
    southwest: LatLngLiteral;
};

enum LocationType {
    APPROXIMATE = 'APPROXIMATE',
    GEOMETRIC_CENTER = 'GEOMETRIC_CENTER',
    RANGE_INTERPOLATED = 'RANGE_INTERPOLATED',
    ROOFTOP = 'ROOFTOP',
}

type AddressGeometry = {
    bounds: LatLngBounds;
    location: LatLngLiteral;
    location_type: LocationType;
    viewport: LatLngBounds;
};

type OpeningHoursTime = {
    day: number;
    time: string;
};

type OpeningPeriod = {
    close: OpeningHoursTime;
    open: OpeningHoursTime;
};

type OpeningHours = {
    open_now: boolean;
    periods: OpeningPeriod[];
    weekday_text: string[];
};

type PlacePhoto = {
    height: number;
    html_attributions: string[];
    photo_reference: string;
    width: number;
};

type PlusCode = {
    compound_code: string;
    global_code: string;
};

type Place = {
    address_components: AddressComponent[];
    adr_address: string;
    business_status: string;
    formatted_address: string;
    formatted_phone_number: string;
    geometry: AddressGeometry;
    icon: string;
    international_phone_number: string;
    name: string;
    opening_hours: OpeningHours;
    permanently_closed: boolean;
    photos: PlacePhoto[];
    place_id: string;
    plus_code: PlusCode;
    price_level: number;
    rating: number;
    types: AddressType[];
    url: string;
    user_ratings_total: number;
    utc_offset: number;
    vicinity: string;
    website: string;
};

type PlaceDetailsResponse = {
    status: PlaceApiStatus;
    html_attributions: string[];
    result: Place;
};

type PlaceApiError = {
    response: {
        data: {
            status: PlaceApiStatus;
            error_message: string;
        };
    };
};

export type { PlaceApiError, PlaceAutocompleteResponse, PlaceDetailsResponse };
export { AddressType, LocationType, PlaceApiStatus };
